// Phase 19 · Community V2 · Admin DM reports queue
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Flag, Eye, Check, Ban, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
}

function RevealModal({ report, onClose, onRevealed }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (reason.trim().length < 3) { toast.error("Reason required (min 3 chars)"); return; }
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/admin/community/dm/reports/${report.id}/reveal`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ reason: reason.trim() }),
            });
            if (r.ok) {
                const d = await r.json();
                toast.success("Content revealed (audit-logged)");
                onRevealed?.(d);
            } else toast.error("Reveal failed");
        } catch { toast.error("Network error"); }
        setBusy(false);
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="reveal-modal">
            <div className="bg-[#111111] border border-[#FAFAFA]/40 rounded-2xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-neutral-100 flex items-center gap-2">
                        <Eye size={16} className="text-[#FAFAFA]" /> Reveal DM content
                    </h3>
                    <button type="button" onClick={onClose}><X size={16} className="text-neutral-400" /></button>
                </div>
                <p className="text-xs text-neutral-400 mb-3">
                    This action is <b className="text-[#FAFAFA]">audit-logged</b> with your user id and the reason.
                    Only use when the report requires content review.
                </p>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="Reason for reveal (mandatory)"
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FAFAFA] mb-3"
                    data-testid="reveal-reason-input"
                />
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-400">Cancel</button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-4 py-2 text-sm bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-white font-semibold rounded disabled:opacity-40"
                        data-testid="reveal-submit"
                    >
                        {busy ? "..." : "Reveal"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ReportRow({ report, onAction }) {
    const [revealed, setRevealed] = useState(null);
    const [revealing, setRevealing] = useState(false);

    const resolve = async (action) => {
        try {
            const r = await fetch(`${API}/api/admin/community/dm/reports/${report.id}/resolve`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ action }),
            });
            if (r.ok) {
                toast.success(`Report ${action}`);
                onAction?.();
            } else toast.error(`Resolve failed`);
        } catch { toast.error("Network error"); }
    };

    return (
        <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4 space-y-2" data-testid={`dm-report-${report.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FAFAFA]/10 text-[#FAFAFA] font-semibold">
                            {report.reason}
                        </span>
                        <span className="text-[10px] text-neutral-500">
                            {fmtDate(report.created_at)}
                        </span>
                        <span className="text-[10px] text-neutral-500">
                            · reporter: @{report.reporter_username || "?"}
                        </span>
                        <span className="text-[10px] text-neutral-500">
                            · sender: @{report.sender_username || "?"}
                        </span>
                    </div>
                    {report.notes && (
                        <p className="text-xs text-neutral-400 mt-1">
                            <span className="text-neutral-500">Reporter note:</span> {report.notes}
                        </p>
                    )}
                    <div className="text-xs text-neutral-500 mt-1">
                        <span>message_type:</span> <b className="text-neutral-300">{report.message_type}</b>
                        <span className="ml-3">sent_at:</span> <b className="text-neutral-300">{fmtDate(report.message_sent_at)}</b>
                    </div>
                    {revealed && (
                        <div className="mt-2 bg-[#111111] border border-[#FAFAFA]/30 rounded-lg p-3">
                            <p className="text-[10px] uppercase tracking-widest text-[#FAFAFA] mb-1">
                                Revealed content (audit-logged)
                            </p>
                            <p className="text-xs text-neutral-200 whitespace-pre-wrap break-words">
                                {revealed.body}
                            </p>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-[#1A1A1A]">
                {!revealed && (
                    <button
                        type="button"
                        onClick={() => setRevealing(true)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#FAFAFA]/10 border border-[#FAFAFA]/40 text-[#FAFAFA] rounded-lg hover:bg-[#FAFAFA]/20"
                        data-testid={`reveal-${report.id}`}
                    >
                        <Eye size={12} /> Reveal
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => resolve("dismiss")}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#1A1A1A] text-neutral-400 hover:text-neutral-200 rounded-lg"
                    data-testid={`dismiss-${report.id}`}
                >
                    <X size={12} /> Dismiss
                </button>
                <button
                    type="button"
                    onClick={() => resolve("hide")}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-neutral-700/20 text-neutral-300 border border-neutral-600/50 rounded-lg hover:bg-neutral-700/40"
                    data-testid={`hide-${report.id}`}
                >
                    <Check size={12} /> Hide message
                </button>
                <button
                    type="button"
                    onClick={() => resolve("ban_user")}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#FAFAFA] text-white font-semibold rounded-lg hover:bg-[#FAFAFA]/80"
                    data-testid={`ban-${report.id}`}
                >
                    <Ban size={12} /> Ban sender
                </button>
            </div>

            {revealing && (
                <RevealModal
                    report={report}
                    onClose={() => setRevealing(false)}
                    onRevealed={(d) => {
                        setRevealed(d);
                        setRevealing(false);
                    }}
                />
            )}
        </div>
    );
}

export default function AdminCommunityDmReports() {
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);

    const load = async () => {
        setLoading(true); setDisabled(false);
        try {
            const r = await fetch(`${API}/api/admin/community/dm/reports?status=${status}&limit=100`, {
                credentials: "include",
            });
            if (r.status === 503) { setDisabled(true); setItems([]); return; }
            if (r.ok) {
                const d = await r.json();
                setItems(d.items || []);
            }
        } catch {}
        setLoading(false);
    };

    useEffect(() => { load(); }, [status]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="admin-dm-reports-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/admin" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Admin
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold flex items-center gap-2">
                        <Flag size={16} className="text-[#FAFAFA]" /> DM reports
                    </h1>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA]">
                        DM disabled — reports frozen.
                    </div>
                )}
                <div className="flex items-center gap-2 text-xs">
                    {["pending", "resolved", "dismissed"].map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setStatus(s)}
                            className={`px-3 py-1.5 rounded-full border transition-all capitalize ${status === s ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FFFFFF]/30"}`}
                            data-testid={`filter-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                {loading && <p className="text-sm text-neutral-500">Loading…</p>}
                {!loading && !items.length && !disabled && (
                    <p className="text-sm text-neutral-500 italic">No {status} reports</p>
                )}
                <div className="space-y-3">
                    {items.map((r) => <ReportRow key={r.id} report={r} onAction={load} />)}
                </div>
            </main>
        </div>
    );
}
